// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Container, Grid } from '@mui/material';
import useSettings from 'src/hooks/useSettings';
import Page from 'src/components/Page';
import NPFXNewProducts from './NPFXNewProducts';
import useLocales from 'src/hooks/useLocales';
import WidgetSummary from './WidgetSummary';

import { useEffect, useState } from 'react';
import { useStore } from 'src/stores/store';
import Loader from 'src/components/loader/Loader';

import StoreIcon from '@mui/icons-material/Store';

import Diversity3Icon from '@mui/icons-material/Diversity3';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import ShopBasedOnStatus from './ShopBasedOnStatus';

import ContractCategorized from './ContractCategorized';
import CustomerCategorized from './CustomerCategorized';
import IncomeChart from './IncomeChart';
import PermissionBasedGuard from 'src/guards/PermissionBasedGuard';
// ----------------------------------------------------------------------

export default function NPFXDashboard() {
  const theme = useTheme();
  const { translate } = useLocales();
  const { Dashboardstore, IncomeStore } = useStore();
  const { loadDashboard, DashboardData } = Dashboardstore;
  const { IncomeChartData, LoadIncomeChart } = IncomeStore;
  const [load, setLoad] = useState<boolean>(true);
  const { themeStretch } = useSettings();

  useEffect(() => {
    setLoad(true);
    setTimeout(() => {
      loadDashboard().then(() => {
        LoadIncomeChart();
        setLoad(false);
      });
    }, 1000);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //For dynamic grid
  const getGridSize = (length: number) => {
    if (length === 1) return 12;
    if (length === 2) return 6;
    if (length === 3) return 4;
    if (length === 4) return 3;

    return 2;
  };

  // const CurrentBalancegridSize = getGridSize(
  //   realTimeDashboardData?.totalMainAssetsCurrentBalance?.length || 0
  // );
  // const ExpensegridSize = getGridSize(realTimeDashboardData?.todayTotalExpense?.length || 0);

  return (
    <Page title="General:">
      {load ? (
        <Loader />
      ) : (
        <Container maxWidth={themeStretch ? false : 'xl'}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <NPFXNewProducts />
            </Grid>
            {/* <PermissionBasedGuard permissions={['DashboardAndReport-RealTimeDashboard']}> */}

            <PermissionBasedGuard
              permissions={['Dashboard-GetContractShopAndCustomerCountDashboard']}
            >
              <Grid item xs={12} md={3}>
                <WidgetSummary
                  title={`${translate('Dashboards.TotalShop')}`}
                  percent={0.6}
                  total={DashboardData?.totalShopCount}
                  chartColor={theme.palette.chart.red[0]}
                  icon={<StoreIcon sx={{ fontSize: 75, color: 'warning.main' }} />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <WidgetSummary
                  title={`${translate('Dashboards.TotalCustomer')}`}
                  percent={0.6}
                  total={DashboardData?.totalCustomers}
                  chartColor={theme.palette.chart.red[0]}
                  icon={<Diversity3Icon sx={{ fontSize: 75, color: 'secondary.main' }} />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <WidgetSummary
                  title={`${translate('Dashboards.ActiveContract')}`}
                  percent={0.6}
                  total={DashboardData?.totalActiveContracts}
                  chartColor={theme.palette.chart.red[0]}
                  icon={<ArticleIcon sx={{ fontSize: 75, color: 'secondary.main' }} />}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <WidgetSummary
                  title={`${translate('Dashboards.CurrentMonthExpiredContract')}`}
                  percent={0.6}
                  total={DashboardData?.currenctMonthExpiredContracts}
                  chartColor={theme.palette.chart.red[0]}
                  icon={
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <DescriptionIcon sx={{ fontSize: 64, color: 'error.main' }} />
                      <HighlightOffIcon
                        sx={{
                          fontSize: 24,
                          color: 'error.dark',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                        }}
                      />
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <ShopBasedOnStatus
                  title={translate('Dashboards.ShopBasedOnStatus')}
                  list={DashboardData?.shopBasedOnStatus}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <ContractCategorized
                  title={translate('Dashboards.ContractCategorized')}
                  list={DashboardData?.contractCategorized}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <CustomerCategorized
                  title={translate('Dashboards.CustomerCategorized')}
                  list={DashboardData?.customerCategorized}
                />
              </Grid>
            </PermissionBasedGuard>
            <PermissionBasedGuard permissions={['Income-IncomeLineChart']}>
              <Grid item xs={12} md={12} lg={12}>
                <IncomeChart
                  title={translate('Dashboards.IncomeChart')}
                  subheader=""
                  chartLabels={IncomeChartData?.chartLabels! || []}
                  chartData={IncomeChartData?.chartData! || []}
                />
              </Grid>
            </PermissionBasedGuard>
          </Grid>
        </Container>
      )}
    </Page>
  );
}
